import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'
import { AyarlarSekmeleri } from './ayarlar-sekmeler'

type Alan = components['schemas']['CustomFieldRead']
type Tur = Alan['field_type']

const TURLER: { deger: Tur; etiket: string; secenekli: boolean }[] = [
  { deger: 'text', etiket: 'Kısa metin', secenekli: false },
  { deger: 'long_text', etiket: 'Uzun metin', secenekli: false },
  { deger: 'number', etiket: 'Sayı', secenekli: false },
  { deger: 'date', etiket: 'Tarih', secenekli: false },
  { deger: 'boolean', etiket: 'Evet / Hayır', secenekli: false },
  { deger: 'select', etiket: 'Tek seçim', secenekli: true },
  { deger: 'multi_select', etiket: 'Çok seçim', secenekli: true },
]

const TUR_ETIKET = new Map(TURLER.map((tur) => [tur.deger, tur.etiket]))

/**
 * Ayarlar › Özel alanlar — YES-105.
 *
 * ADR-1'in ikinci yarısı: eski sistemlerde `glnNumber`, `pharmacyName` gibi
 * derneğe özgü alanlar çekirdek tabloya kolon olarak eklenmişti. Burada her
 * dernek kendi alanlarını tanımlıyor, şema değişmiyor.
 */
export function AyarlarOzelAlanlar() {
  const slug = useTenantSlug()
  const [ekleAcik, setEkleAcik] = useState(false)
  const [duzenlenen, setDuzenlenen] = useState<Alan | null>(null)

  const alanlar = useQuery({
    queryKey: ['custom-fields', slug, 'all'],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/custom-fields', {
        params: { query: { entity_type: 'person' } },
      })
      return data ?? []
    },
  })

  if (alanlar.isError) {
    const yetkisiz = alanlar.error instanceof ApiError && alanlar.error.problem.status === 403
    return (
      <Sayfa>
        <Card>
          <CardBody className="text-sm">
            {yetkisiz ? 'Bu ekranı görme yetkiniz yok.' : 'Özel alanlar yüklenemedi.'}
          </CardBody>
        </Card>
      </Sayfa>
    )
  }

  return (
    <Sayfa>
      <Card>
        <CardHeader
          title="Üyeye özel alanlar"
          description="Derneğinizin üye formuna kendi alanlarınızı ekleyin. Alan kodu sonradan değişmez."
          action={<Button onClick={() => setEkleAcik(true)}>Alan ekle</Button>}
        />
        <CardBody>
          {alanlar.data && alanlar.data.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {alanlar.data.map((alan) => (
                <li key={alan.id}>
                  <button
                    type="button"
                    onClick={() => setDuzenlenen(alan)}
                    className="flex w-full flex-wrap items-center gap-3 rounded-[var(--radius-control)] border border-[var(--ad-border)] px-3 py-2.5 text-left hover:bg-[var(--ad-surface-muted)]"
                  >
                    <span className="flex min-w-48 flex-col">
                      <span className="text-sm font-medium">{alan.label}</span>
                      <span className="font-mono text-xs text-[var(--ad-text-muted)]">
                        {alan.key}
                      </span>
                    </span>
                    <Badge tone="neutral">
                      {TUR_ETIKET.get(alan.field_type) ?? alan.field_type}
                    </Badge>
                    {alan.is_required && <Badge tone="info">Zorunlu</Badge>}
                    {!alan.is_active && (
                      <Badge tone="warning" className="ml-auto">
                        Gizli
                      </Badge>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={<Icon name="filter" size={24} aria-hidden />}
              title="Özel alan yok"
              description="Üye formunda derneğinize özgü bir bilgi tutmanız gerekiyorsa buradan ekleyin."
            />
          )}
        </CardBody>
      </Card>

      {ekleAcik && <AlanEkle slug={slug} onKapat={() => setEkleAcik(false)} />}
      {duzenlenen && (
        <AlanDuzenle alan={duzenlenen} slug={slug} onKapat={() => setDuzenlenen(null)} />
      )}
    </Sayfa>
  )
}

type Secenek = { value: string; label: string }

function SecenekDuzenleyici({
  secenekler,
  onDegis,
}: {
  secenekler: Secenek[]
  onDegis: (secenekler: Secenek[]) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">Seçenekler</p>
      {secenekler.map((secenek, sira) => (
        <div key={sira} className="flex items-center gap-2">
          <Input
            value={secenek.label}
            placeholder="Görünen ad"
            onChange={(event) => {
              const sonraki = [...secenekler]
              // Kod, görünen addan türetiliyor ama SABİTLENİYOR: kaydedildikten
              // sonra adı değiştirmek, girilmiş değerleri geçersiz kılardı.
              sonraki[sira] = {
                label: event.target.value,
                value: secenek.value || kodlastir(event.target.value),
              }
              onDegis(sonraki)
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onDegis(secenekler.filter((_, index) => index !== sira))}
          >
            Kaldır
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onDegis([...secenekler, { value: '', label: '' }])}
        >
          Seçenek ekle
        </Button>
      </div>
    </div>
  )
}

function kodlastir(metin: string): string {
  return (
    metin
      .trim()
      .toLocaleLowerCase('tr')
      .replace(/ı/g, 'i')
      .replace(/ş/g, 's')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'secenek'
  )
}

function AlanEkle({ slug, onKapat }: { slug: string | null; onKapat: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [tur, setTur] = useState<Tur>('text')
  const [secenekler, setSecenekler] = useState<Secenek[]>([])
  const [hatalar, setHatalar] = useState<Record<string, string>>({})
  const [hata, setHata] = useState<string | null>(null)

  const secenekli = TURLER.find((item) => item.deger === tur)?.secenekli ?? false

  const kaydet = useMutation({
    mutationFn: async (form: FormData) => {
      await api.POST('/api/v1/custom-fields', {
        body: {
          entity_type: 'person',
          key: String(form.get('key')),
          label: String(form.get('label')),
          field_type: tur,
          options: secenekli ? secenekler.filter((secenek) => secenek.label.trim()) : [],
          is_required: form.get('is_required') !== null,
          sort_order: Number(form.get('sort_order') || 0),
        },
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['custom-fields', slug] })
      toast({ title: 'Alan eklendi', tone: 'success' })
      onKapat()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setHatalar(error.fieldErrors())
        setHata(error.problem.detail)
      }
      toast({ title: 'Eklenemedi', tone: 'error' })
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(acik) => !acik && onKapat()}
      title="Özel alan ekle"
      description="Kod ve tür sonradan DEĞİŞMEZ: ikisi de girilmiş değerlerin anlamını taşır."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setHata(null)
          kaydet.mutate(new FormData(event.currentTarget))
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Görünen ad" required error={hatalar.label}>
            {(props) => <Input name="label" autoFocus placeholder="Meslek" {...props} />}
          </Field>
          <Field
            label="Kod"
            required
            error={hatalar.key}
            hint="Küçük harf ve alt çizgi. Dışa aktarımda sütun başlığı olur."
          >
            {(props) => <Input name="key" placeholder="meslek" {...props} />}
          </Field>
        </div>

        <Field label="Tür" required>
          {(props) => (
            <select
              value={tur}
              onChange={(event) => setTur(event.target.value as Tur)}
              className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
              {...props}
            >
              {TURLER.map((item) => (
                <option key={item.deger} value={item.deger}>
                  {item.etiket}
                </option>
              ))}
            </select>
          )}
        </Field>

        {secenekli && <SecenekDuzenleyici secenekler={secenekler} onDegis={setSecenekler} />}

        <div className="grid items-end gap-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="is_required" />
            Zorunlu alan
          </label>
          <Field label="Sıra" hint="Küçük olan üstte">
            {(props) => <Input name="sort_order" type="number" defaultValue="0" {...props} />}
          </Field>
        </div>

        {/* Zorunluluk sonradan eklenirse eski kayıtları etkilemiyor; bunu
            söylemek, "eski üyeleri düzenleyemiyorum" sorusunu önlüyor. */}
        <p className="text-xs text-[var(--ad-text-muted)]">
          Zorunlu alan yalnızca YENİ kayıtlarda istenir. Mevcut üyeleri düzenlemeyi engellemez.
        </p>

        {hata && (
          <p role="alert" className="text-xs text-[var(--ad-danger)]">
            {hata}
          </p>
        )}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onKapat}>
            Vazgeç
          </Button>
          <Button type="submit" disabled={kaydet.isPending}>
            {kaydet.isPending ? 'Ekleniyor…' : 'Ekle'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function AlanDuzenle({
  alan,
  slug,
  onKapat,
}: {
  alan: Alan
  slug: string | null
  onKapat: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [secenekler, setSecenekler] = useState<Secenek[]>(
    alan.options.map((secenek) => ({
      value: String(secenek.value),
      label: String(secenek.label),
    })),
  )
  const [hata, setHata] = useState<string | null>(null)

  const secenekli = alan.field_type === 'select' || alan.field_type === 'multi_select'

  // Kaç kayıtta değeri var? Silinebilir mi sorusunun cevabı bu.
  const ayrinti = useQuery({
    queryKey: ['custom-field', alan.id],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/custom-fields/{definition_id}', {
        params: { path: { definition_id: alan.id } },
      })
      return data
    },
  })

  const tazele = () => {
    void queryClient.invalidateQueries({ queryKey: ['custom-fields', slug] })
    onKapat()
  }

  const kaydet = useMutation({
    mutationFn: async (form: FormData) => {
      await api.PATCH('/api/v1/custom-fields/{definition_id}', {
        params: { path: { definition_id: alan.id } },
        body: {
          label: String(form.get('label')),
          is_required: form.get('is_required') !== null,
          is_active: form.get('is_active') !== null,
          sort_order: Number(form.get('sort_order') || 0),
          ...(secenekli ? { options: secenekler.filter((secenek) => secenek.label.trim()) } : {}),
        },
      })
    },
    onSuccess: () => {
      tazele()
      toast({ title: 'Alan güncellendi', tone: 'success' })
    },
    onError: (error) =>
      setHata(error instanceof ApiError ? error.problem.detail : 'Kaydedilemedi.'),
  })

  const sil = useMutation({
    mutationFn: async () => {
      await api.DELETE('/api/v1/custom-fields/{definition_id}', {
        params: { path: { definition_id: alan.id } },
      })
    },
    onSuccess: () => {
      tazele()
      toast({ title: 'Alan silindi', tone: 'success' })
    },
    onError: (error) => setHata(error instanceof ApiError ? error.problem.detail : 'Silinemedi.'),
  })

  const degerSayisi = ayrinti.data?.value_count ?? 0

  return (
    <Dialog
      open
      onOpenChange={(acik) => !acik && onKapat()}
      title={alan.label}
      description={`${alan.key} · ${TUR_ETIKET.get(alan.field_type) ?? alan.field_type}`}
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setHata(null)
          kaydet.mutate(new FormData(event.currentTarget))
        }}
      >
        <Field label="Görünen ad" required>
          {(props) => <Input name="label" defaultValue={alan.label} autoFocus {...props} />}
        </Field>

        {secenekli && <SecenekDuzenleyici secenekler={secenekler} onDegis={setSecenekler} />}

        <div className="grid items-end gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="is_required" defaultChecked={alan.is_required} />
              Zorunlu alan
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="is_active" defaultChecked={alan.is_active} />
              Formda görünsün
            </label>
          </div>
          <Field label="Sıra">
            {(props) => (
              <Input
                name="sort_order"
                type="number"
                defaultValue={String(alan.sort_order)}
                {...props}
              />
            )}
          </Field>
        </div>

        {hata && (
          <p role="alert" className="text-xs text-[var(--ad-danger)]">
            {hata}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--ad-border)] pt-3">
          {/* Değeri olan alan SİLİNEMEZ: tanımı silmek onlarca üyenin
              verisini yok eder. Doğru düğme baştan gösteriliyor, kullanıcıyı
              önce "Sil"e tıklatıp sonra hata döndürmek yerine. */}
          {degerSayisi > 0 ? (
            <p className="text-xs text-[var(--ad-text-muted)]">
              {degerSayisi} üyede değeri var; silinemez. &quot;Formda görünsün&quot; işaretini
              kaldırarak formdan çıkarabilirsiniz — girilmiş veriler durur.
            </p>
          ) : (
            <Button
              type="button"
              variant="ghost"
              disabled={sil.isPending}
              onClick={() => sil.mutate()}
            >
              Sil
            </Button>
          )}

          <div className="ml-auto flex gap-2">
            <Button type="button" variant="secondary" onClick={onKapat}>
              Vazgeç
            </Button>
            <Button type="submit" disabled={kaydet.isPending}>
              {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  )
}

function Sayfa({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">Ayarlar</h1>
      <AyarlarSekmeleri />
      {children}
    </div>
  )
}
